import { useMemo } from "react";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Notice, NoticeText } from "components/ui/Notice";
import { Pill, PILL_COLORS } from "components/ui/Pill";

import styles from "../../HealthlinkDetailsPage.module.css";
import { formatDate } from "../../utils/formatDate";

const isSuccessCode = (httpResponseCode) =>
  httpResponseCode >= 200 && httpResponseCode < 300;

const isErrorCode = (httpResponseCode) => httpResponseCode >= 400;

const StatusCell = ({ call }) => {
  if (isSuccessCode(call.httpResponseCode)) {
    return <Pill {...PILL_COLORS.statusSuccess}>Success</Pill>;
  }

  if (isErrorCode(call.httpResponseCode)) {
    return <Pill {...PILL_COLORS.statusError}>Failed</Pill>;
  }

  return (
    <Pill {...PILL_COLORS.statusNoStatus}>{call.status || "Unknown"}</Pill>
  );
};

const ResponseCodeCell = ({ call }) => {
  const codeClass = isSuccessCode(call.httpResponseCode)
    ? styles.codeSuccess
    : isErrorCode(call.httpResponseCode)
    ? styles.codeError
    : styles.codeNeutral;

  return <span className={codeClass}>{call.httpResponseCode || "-"}</span>;
};

export const HealthlinkCallsTab = ({
  data,
  totalRecords,
  isLoading,
  currentPage,
  rowsPerPage,
  startDate,
  endDate,
  onPageChange,
  onRowsChange,
  onStartDateChange,
  onEndDateChange,
  onClearDateFilters,
}) => {
  const columns = useMemo(
    () => [
      {
        id: "callTime",
        header: "Time",
        width: "minmax(150px, 1fr)",
        render: (call) => formatDate(call.callTime),
      },
      {
        id: "status",
        header: "Status",
        width: "130px",
        wrap: true,
        render: (call) => <StatusCell call={call} />,
      },
      {
        id: "method",
        header: "Method",
        width: "minmax(120px, 1fr)",
        render: (call) => call.method || "-",
      },
      {
        id: "httpResponseCode",
        header: "Code",
        width: "80px",
        render: (call) => <ResponseCodeCell call={call} />,
      },
      {
        id: "errorMessage",
        header: "Error Message",
        width: "minmax(200px, 2fr)",
        getTooltipText: (call) => call.errorMessage || "-",
        render: (call) => call.errorMessage || "-",
      },
    ],
    []
  );

  return (
    <div className={styles.content}>
      <Notice tone="info" testId="healthlink-calls-notice">
        <NoticeText>
          View all Healthlink calls made by your practice. You can filter by
          date range to find specific calls.
        </NoticeText>
      </Notice>

      <div className={styles.callsFilters} data-testid="healthlink-calls-filters">
        <div className={styles.callsFilterField}>
          <label className={styles.callsFilterLabel}>Start Date</label>
          <div className={styles.callsDatePicker}>
            <CustomCalendar
              value={startDate}
              onChange={onStartDateChange}
              placeholder="Select start date"
              maxDate={endDate || new Date()}
            />
          </div>
        </div>
        <div className={styles.callsFilterField}>
          <label className={styles.callsFilterLabel}>End Date</label>
          <div className={styles.callsDatePicker}>
            <CustomCalendar
              value={endDate}
              onChange={onEndDateChange}
              placeholder="Select end date"
              minDate={startDate}
              maxDate={new Date()}
            />
          </div>
        </div>
        <Button
          variant="primary"
          className={styles.callsClearButton}
          onClick={onClearDateFilters}
        >
          Clear Filters
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        loadingTitle="Loading healthlink calls..."
        loadingText="Please wait."
        emptyTitle="No healthlink calls found"
        emptyText="No healthlink calls were made in the selected date range."
        currentPage={currentPage}
        totalCount={totalRecords}
        rowsValue={rowsPerPage}
        onPageChange={onPageChange}
        onRowsChange={onRowsChange}
      />
    </div>
  );
};
